import { shareApi } from './services/shareApi';
import { SHARE_USE_STUB } from './config';
import { getPendingShareAccept, setPendingShareAccept, clearPendingShareAccept } from './pendingShare';

// A definite 4xx means the server gave a final answer (SUBSCRIPTION_REQUIRED,
// an expired share) that will just fail again — only 5xx is worth retrying.
// A client-side TIMEOUT is explicitly NOT retryable: accept is a slow,
// synchronous course generation, so a timeout means we gave up on a request
// the server is most likely still completing. Re-queueing it is how one click
// becomes three billed generations. A genuine network failure (offline, DNS)
// still throws without a code and stays retryable.
function isRetryable(err) {
  if (err?.code === 'TIMEOUT') return false;
  return !err?.status || err.status >= 500;
}

// Fires a queued share acceptance, if one is pending. Returns the path to send
// the user to (their freshly-generated course), or null if nothing was pending
// or the accept failed. Callers must already be authenticated + subscribed —
// the accept endpoint enforces both. Used by SubscriptionSuccessPage right after
// checkout completes, so the course is created "the moment they subscribe."
export async function resumePendingShareAccept({ authEnabled = true, uid = null } = {}) {
  const token = getPendingShareAccept({ uid });
  if (!token) return null;
  // Clear before the call, not after: if the client times out on a request
  // the server actually completed, there's no leftover token for a later
  // retry (or the resumer firing again) to re-send accept and double-generate
  // the course.
  clearPendingShareAccept();
  try {
    let res;
    if (SHARE_USE_STUB) {
      await new Promise((r) => setTimeout(r, 800));
      res = { courseId: null }; // no real course exists in stub mode
    } else {
      res = await shareApi.acceptShare({ token, auth: authEnabled });
    }
    return res?.courseId ? `/courses/${res.courseId}` : '/dashboard';
  } catch (err) {
    if (isRetryable(err)) {
      // The request may never have reached the server — re-queue so the
      // user can retry from the share link.
      setPendingShareAccept(token, { uid });
    }
    return null;
  }
}
